import { UserRole, useGetUsersListQuery } from 'gql/types';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import { IRecruiterDashboardSettings } from '../../../hooks/useRecruiterDashboardSettings';
import { IRecruiterFilters } from '../utils/recruiterFilters';

export interface IRoleRestriction {
  lockToRegion: boolean;
  lockToOffice: boolean;
  hideRollingDates: boolean;
  hideCalendarDates: boolean;
  showLocationRecruitStats: boolean;
}

export function useRecruiterUserContext(
  setFilters: Dispatch<SetStateAction<IRecruiterFilters>>,
  settings: IRecruiterDashboardSettings | null
) {
  const { data: currentUser } = useCurrentUser();

  const isActualAdmin = currentUser?.role?.value === UserRole.Admin;
  const isSimulating =
    isActualAdmin &&
    !!settings?.simulateRole &&
    settings.simulateRole !== UserRole.Admin;

  const { data: simulatedUserData } = useGetUsersListQuery({
    variables: {
      input: {
        filter: { ids: [settings?.simulateUserId || ''] },
        take: 1,
      },
    },
    skip: !isSimulating || !settings?.simulateUserId,
  });
  const simulatedUser = useMemo(
    () => simulatedUserData?.getUsersList?.data?.[0] || null,
    [simulatedUserData]
  );

  const userRole = isSimulating
    ? settings!.simulateRole!
    : currentUser?.role?.value;
  const userRegionId =
    isSimulating && simulatedUser
      ? simulatedUser.assignedOffice?.region?.id
      : currentUser?.assignedOffice?.region?.id;
  const userOfficeId =
    isSimulating && simulatedUser
      ? simulatedUser.assignedOffice?.id
      : currentUser?.assignedOffice?.id;

  const roleRestriction: IRoleRestriction = useMemo(() => {
    if (!settings?.roleRestrictionsEnabled || !userRole) {
      return {
        lockToRegion: false,
        lockToOffice: false,
        hideRollingDates: false,
        hideCalendarDates: false,
        showLocationRecruitStats: userRole === UserRole.Admin,
      };
    }
    const r = settings.roleRestrictions[userRole];
    return {
      lockToRegion: r?.lockToRegion ?? false,
      lockToOffice: r?.lockToOffice ?? false,
      hideRollingDates: r?.hideRollingDates ?? false,
      hideCalendarDates: r?.hideCalendarDates ?? false,
      showLocationRecruitStats:
        r?.showLocationRecruitStats ?? userRole === UserRole.Admin,
    };
  }, [settings, userRole]);

  const isRegionLocked = roleRestriction.lockToRegion && !!userRegionId;
  const isOfficeLocked = roleRestriction.lockToOffice && !!userOfficeId;

  useEffect(() => {
    if (!settings || !currentUser) return;

    setFilters((prev) => {
      const newFilters = { ...prev };
      if (isRegionLocked && userRegionId) {
        newFilters.regionIds = [userRegionId];
      }
      if (isOfficeLocked && userOfficeId) {
        newFilters.officeIds = [userOfficeId];
      }
      return newFilters;
    });
  }, [
    settings,
    currentUser,
    isRegionLocked,
    isOfficeLocked,
    userRegionId,
    userOfficeId,
  ]);

  return {
    userRole,
    userRegionId,
    userOfficeId,
    roleRestriction,
    isRegionLocked,
    isOfficeLocked,
    isSimulating,
    simulatedUser,
  };
}
